// frontend/src/features/regions/components/RegionsMap.tsx
import React, { useState, useEffect, useMemo } from 'react';
import {
    ComposableMap,
    Geographies,
    Geography,
    Marker,
    ZoomableGroup
} from 'react-simple-maps';
import { scaleSqrt } from 'd3-scale';
import { ZoomOutMap } from '@mui/icons-material';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { ClientData } from '../services/regionsAPI';

const BRAZIL_URL = "/data/brazil-geo-simplified.json";
const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

interface RegionsMapProps {
    clients: ClientData[];
    selectedState: string | null;
    selectedClientName?: string | null;
    highlightedClientNames?: string[] | null;
    inactiveClientToShow?: any | null;
}

interface MunicipalityCoord {
    codigo_ibge: number;
    nome: string;
    latitude: number;
    longitude: number;
    codigo_uf: number;
}

// Predefined approximate center coordinates and zoom levels for each Brazilian state (UF)
const STATE_CENTERS: Record<string, { center: [number, number]; zoom: number }> = {
    AC: { center: [-70, -9], zoom: 4.5 },
    AL: { center: [-36.5, -9.6], zoom: 7.5 },
    AM: { center: [-64, -4], zoom: 4.5 },
    AP: { center: [-51.5, 1.4], zoom: 5.5 },
    BA: { center: [-41.7, -12.5], zoom: 4.5 },
    CE: { center: [-39.5, -5.2], zoom: 5.5 },
    DF: { center: [-47.9, -15.8], zoom: 9.5 },
    ES: { center: [-40.3, -19.8], zoom: 6.5 },
    GO: { center: [-49.6, -15.7], zoom: 5 },
    MA: { center: [-45.2, -5.3], zoom: 5 },
    MG: { center: [-44.5, -18.5], zoom: 5 },
    MS: { center: [-54.6, -20.5], zoom: 5 },
    MT: { center: [-56.0, -12.6], zoom: 4.5 },
    PA: { center: [-52.5, -5.5], zoom: 4.5 },
    PB: { center: [-36.8, -7.2], zoom: 6.5 },
    PE: { center: [-37.5, -8.3], zoom: 6 },
    PI: { center: [-42.5, -7.5], zoom: 5 },
    PR: { center: [-51.5, -24.8], zoom: 5.5 },
    RJ: { center: [-42.7, -22.3], zoom: 7.5 },
    RN: { center: [-36.5, -5.7], zoom: 6.5 },
    RO: { center: [-62.5, -11.2], zoom: 5.5 },
    RR: { center: [-61.3, 2.1], zoom: 5 },
    RS: { center: [-52.5, -30.0], zoom: 5.5 },
    SC: { center: [-50.5, -27.2], zoom: 6 },
    SE: { center: [-37.1, -10.6], zoom: 7.5 },
    SP: { center: [-48.5, -22.2], zoom: 5.5 },
    TO: { center: [-48.3, -10.2], zoom: 5 }
};

const UF_CODES: Record<number, string> = {
    11: 'RO', 12: 'AC', 13: 'AM', 14: 'RR', 15: 'PA', 16: 'AP', 17: 'TO',
    21: 'MA', 22: 'PI', 23: 'CE', 24: 'RN', 25: 'PB', 26: 'PE', 27: 'AL',
    28: 'SE', 29: 'BA', 31: 'MG', 32: 'ES', 33: 'RJ', 35: 'SP', 41: 'PR',
    42: 'SC', 43: 'RS', 50: 'MS', 51: 'MT', 52: 'GO', 53: 'DF'
};

const normalizeString = (str: string) => {
    if (!str) return '';
    return str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // remove accents
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '') // strip symbols
        .trim();
};

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', {
        style: 'currency', currency: 'BRL',
        notation: 'compact', compactDisplay: 'short'
    }).format(value);

const RegionsMap: React.FC<RegionsMapProps> = ({ clients, selectedState, selectedClientName, highlightedClientNames, inactiveClientToShow }) => {
    const [municipios, setMunicipios] = useState<MunicipalityCoord[]>([]);
    const [loading, setLoading] = useState(true);

    // Zoomable group coordinates state
    const [position, setPosition] = useState({ coordinates: [-55, -15] as [number, number], zoom: 1 });

    // Load coordinates dictionary from public folder
    useEffect(() => {
        const loadCoords = async () => {
            try {
                const res = await fetch('/data/municipios.json');
                const json = await res.json();
                setMunicipios(json);
            } catch (err) {
                console.error("Erro ao carregar dicionário de municípios:", err);
            } finally {
                setLoading(false);
            }
        };
        loadCoords();
    }, []);

    // Create lookup Maps for fast O(1) coordination queries
    const { municipiosMap, municipiosMapByName } = useMemo(() => {
        const mId = new Map<number, MunicipalityCoord>();
        const mName = new Map<string, MunicipalityCoord>();

        municipios.forEach(m => {
            mId.set(m.codigo_ibge, m);

            const ufInitials = UF_CODES[m.codigo_uf];
            if (ufInitials) {
                const nameKey = `${normalizeString(m.nome)}_${ufInitials}`;
                mName.set(nameKey, m);
            }
        });

        return { municipiosMap: mId, municipiosMapByName: mName };
    }, [municipios]);

    // Resolve target state from active/inactive client click
    const clientStateCode = useMemo(() => {
        if (selectedClientName) {
            const found = clients.find(c => c.cliente === selectedClientName);
            return found?.uf ? found.uf.toUpperCase() : null;
        }
        if (inactiveClientToShow) {
            return inactiveClientToShow.uf ? inactiveClientToShow.uf.toUpperCase() : null;
        }
        return null;
    }, [selectedClientName, inactiveClientToShow, clients]);

    // Resolve target states from active product (model) filter
    const modelStateCodes = useMemo(() => {
        if (!highlightedClientNames || highlightedClientNames.length === 0) return new Set<string>();
        const states = new Set<string>();
        clients.forEach(c => {
            if (highlightedClientNames.includes(c.cliente) && c.uf) {
                states.add(c.uf.toUpperCase());
            }
        });
        return states;
    }, [highlightedClientNames, clients]);

    // Build Set of UFs with active sales in the current view for color high-contrasting
    const activeUFs = useMemo(() => {
        return new Set(clients.map(c => c.uf.toUpperCase()));
    }, [clients]);

    // Dynamically adjust map focus based on selected state from parent filter or child selection
    useEffect(() => {
        let targetState = selectedState;
        
        if (!targetState && clientStateCode) {
            targetState = clientStateCode;
        } else if (!targetState && modelStateCodes.size === 1) {
            targetState = Array.from(modelStateCodes)[0];
        } else if (!targetState && activeUFs.size === 1) {
            targetState = Array.from(activeUFs)[0];
        }

        if (targetState && STATE_CENTERS[targetState]) {
            const stateConfig = STATE_CENTERS[targetState];
            setPosition({ coordinates: stateConfig.center, zoom: stateConfig.zoom });
        } else {
            setPosition({ coordinates: [-55, -15], zoom: 1 });
        }
    }, [selectedState, clientStateCode, modelStateCodes, activeUFs]);

    // Cross-reference clients list with coordinates and apply jitter/offset for overlapping points
    const markers = useMemo(() => {
        const listToMap = [...clients];
        if (inactiveClientToShow) {
            const exists = clients.some(c => c.cliente === inactiveClientToShow.name);
            if (!exists) {
                listToMap.push({
                    cliente: inactiveClientToShow.name,
                    uf: inactiveClientToShow.uf,
                    cidade: inactiveClientToShow.cidade,
                    ibge: inactiveClientToShow.ibge,
                    totalVenda: inactiveClientToShow.totalHistorico,
                    totalMargem: 0,
                    participacao: 0,
                    isInactive: true
                } as any);
            }
        }

        if (listToMap.length === 0 || municipiosMap.size === 0) return [];

        // 1. First map to base coordinates using robust state-matching fallback
        const mapped = listToMap
            .map(client => {
                let coord: any = null;
                const clientUfUpper = client.uf.toUpperCase();

                // A. Check IBGE code first, but only if the state code matches the client's UF!
                if (client.ibge) {
                    const match = municipiosMap.get(client.ibge);
                    if (match) {
                        const ibgeUf = UF_CODES[match.codigo_uf];
                        if (ibgeUf === clientUfUpper) {
                            coord = match;
                        }
                    }
                }

                // B. Fallback: Search by normalized city name and state (UF)
                if (!coord && client.cidade) {
                    const lookupKey = `${normalizeString(client.cidade)}_${clientUfUpper}`;
                    coord = municipiosMapByName.get(lookupKey);
                }

                // C. Last-resort fallback: Use raw IBGE coordinate even if UF mismatched
                if (!coord && client.ibge) {
                    coord = municipiosMap.get(client.ibge);
                }

                if (!coord) return null;

                return {
                    ...client,
                    baseCoordinates: [coord.longitude, coord.latitude] as [number, number]
                };
            })
            .filter(m => m !== null) as Array<ClientData & { baseCoordinates: [number, number] }>;

        // 2. Group by IBGE code to detect duplicates
        const ibgeGroups: Record<number, typeof mapped> = {};
        mapped.forEach(item => {
            const code = item.ibge!;
            if (!ibgeGroups[code]) {
                ibgeGroups[code] = [];
            }
            ibgeGroups[code].push(item);
        });

        // 3. Decide entre modo agregado (visão geral) ou individual (com filtro ativo)
        const hasActiveFilter = !!selectedState || !!selectedClientName || !!inactiveClientToShow || 
            (!!highlightedClientNames && highlightedClientNames.length > 0);

        const markersOutput: Array<any> = [];

        if (!hasActiveFilter && Object.keys(ibgeGroups).length > 20) {
            // ── MODO AGREGADO: um marcador por município ──
            // Reduz drasticamente a quantidade de SVG para o mapa ficar responsivo
            Object.entries(ibgeGroups).forEach(([ibgeStr, group]) => {
                const totalVenda = group.reduce((sum, c) => sum + Number(c.totalVenda), 0);
                const totalMargem = group.reduce((sum, c) => sum + Number(c.totalMargem), 0);
                markersOutput.push({
                    cliente: `${group[0].cidade || ''} - ${group[0].uf}`,
                    uf: group[0].uf,
                    cidade: group[0].cidade,
                    ibge: Number(ibgeStr),
                    totalVenda,
                    totalMargem,
                    participacao: 0,
                    totalClientes: group.length,
                    coordinates: group[0].baseCoordinates,
                    isAggregated: true,
                });
            });
        } else {
            // ── MODO INDIVIDUAL: um marcador por cliente (com espiral para duplicatas) ──
            Object.entries(ibgeGroups).forEach(([ibgeStr, group]) => {
                const count = group.length;
                if (count === 1) {
                    markersOutput.push({
                        ...group[0],
                        coordinates: group[0].baseCoordinates
                    });
                } else {
                    group.forEach((item, index) => {
                        const angle = (2 * Math.PI * index) / count;
                        const radius = 0.07 + (index * 0.015);
                        const offsetLon = item.baseCoordinates[0] + radius * Math.cos(angle);
                        const offsetLat = item.baseCoordinates[1] + radius * Math.sin(angle);
                        markersOutput.push({
                            ...item,
                            coordinates: [offsetLon, offsetLat]
                        });
                    });
                }
            });
        }

        // Sort markers so smaller circles are drawn on top of larger circles!
        return markersOutput.sort((a, b) => b.totalVenda - a.totalVenda);
    }, [clients, municipiosMap, inactiveClientToShow, selectedState, selectedClientName, highlightedClientNames]);


    // Calculate dynamic radius using d3-scale
    const radiusScale = useMemo(() => {
        if (markers.length === 0) return () => 3;
        const maxVal = Math.max(...markers.map(m => m.totalVenda));
        return scaleSqrt()
            .domain([0, maxVal])
            .range([3, 12]); // Min 3px, Max 12px (much smaller and elegant)
    }, [markers]);

    const handleResetZoom = () => {
        if (selectedState && STATE_CENTERS[selectedState]) {
            const stateConfig = STATE_CENTERS[selectedState];
            setPosition({ coordinates: stateConfig.center, zoom: stateConfig.zoom });
        } else if (activeUFs.size === 1) {
            const stateConfig = STATE_CENTERS[Array.from(activeUFs)[0]];
            if (stateConfig) setPosition({ coordinates: stateConfig.center, zoom: stateConfig.zoom });
            else setPosition({ coordinates: [-55, -15], zoom: 1 });
        } else {
            setPosition({ coordinates: [-55, -15], zoom: 1 });
        }
    };

    const handleMoveEnd = (newPosition: any) => {
        setPosition(newPosition);
    };

    const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
        if (typeof document === 'undefined') return false;
        return document.documentElement.getAttribute('data-theme') === 'dark';
    });

    useEffect(() => {
        if (typeof document === 'undefined') return;

        const checkTheme = () => {
            const currentTheme = document.documentElement.getAttribute('data-theme');
            setIsDarkMode(currentTheme === 'dark');
        };

        checkTheme();

        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
                    checkTheme();
                }
            }
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme']
        });

        return () => observer.disconnect();
    }, []);

    if (loading) {
        return (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', fontSize: '13px', fontFamily: FONT }}>
                Carregando dicionário geográfico...
            </div>
        );
    }

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: isDarkMode ? '#0f172a' : '#cbd5e1', borderRadius: 'var(--radius-base, 12px)' }}>
            <button
                onClick={handleResetZoom}
                style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    zIndex: 10,
                    background: 'var(--bg-widget)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-base, 8px)',
                    padding: '8px',
                    cursor: 'pointer',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--text-primary)',
                    transition: 'all 0.2s',
                    outline: 'none'
                }}
                title="Resetar Zoom"
            >
                <ZoomOutMap sx={{ fontSize: 18 }} />
            </button>

            <ComposableMap
                projection="geoMercator"
                projectionConfig={{
                    scale: 620,
                    center: [0, 0]
                }}
                style={{
                    width: "100%",
                    height: "100%"
                }}
            >
                <ZoomableGroup
                    zoom={position.zoom}
                    center={position.coordinates}
                    onMoveEnd={handleMoveEnd}
                    maxZoom={40} // Greatly increased max zoom to allow super close inspection
                    minZoom={0.8}
                >
                    <Geographies geography={BRAZIL_URL}>
                        {({ geographies }: { geographies: any[] }) =>
                            geographies && geographies.length > 0 ? (
                                geographies.map((geo) => {
                                    const ufCode = geo.properties.sigla; // state initials e.g. "SP"
                                    const ufCodeUpper = ufCode.toUpperCase();
                                    const isSelected = selectedState === ufCode || 
                                                     clientStateCode === ufCodeUpper || 
                                                     modelStateCodes.has(ufCodeUpper);
                                    const hasSales = activeUFs.has(ufCodeUpper);

                                    // Adaptative dark/light theme coloring for states
                                    let fill = isDarkMode ? '#334155' : '#475569';
                                    let stroke = isDarkMode ? '#1e293b' : '#0f172a';
                                    let strokeWidth = 0.8;

                                    if (isSelected) {
                                        fill = 'var(--theme-accent, #1e293b)';
                                        stroke = 'var(--theme-chart-start, #dc2626)';
                                        strokeWidth = 1.5;
                                    } else if (!hasSales) {
                                        fill = isDarkMode ? '#1e293b' : '#94a3b8';
                                        stroke = isDarkMode ? '#0f172a' : '#475569';
                                        strokeWidth = 0.4;
                                    }

                                    return (
                                        <Geography
                                            key={geo.rsmKey}
                                            geography={geo}
                                            fill={fill}
                                            stroke={stroke}
                                            strokeWidth={strokeWidth}
                                            style={{
                                                default: { outline: "none", transition: 'fill 0.2s ease', vectorEffect: 'non-scaling-stroke' },
                                                hover: { outline: "none", fill: isSelected ? 'var(--theme-accent, #1e293b)' : (isDarkMode ? '#475569' : '#334155'), vectorEffect: 'non-scaling-stroke' },
                                                pressed: { outline: "none", vectorEffect: 'non-scaling-stroke' }
                                             }}
                                        />
                                    );
                                })
                            ) : null
                        }
                    </Geographies>

                    {markers.map((marker, index) => {
                        // Dynamically scale down the radius as zoom increases, using Math.pow
                        // This prevents giant circles at high zoom levels, letting them resolve clearly
                        const baseRadius = radiusScale(marker.totalVenda);
                        const radius = baseRadius / Math.pow(position.zoom, 1.2);
                        
                        // Minimum size is 0.5px (to keep them very small and elegant when zoomed in)
                        const finalRadius = Math.max(0.5, radius);

                        // Highlighting logic
                        const isMarkerInactive = (marker as any).isInactive;
                        const hasFilter = !!selectedClientName || (highlightedClientNames && highlightedClientNames.length > 0) || !!inactiveClientToShow;
                        let isHighlighted = false;
                        if (hasFilter) {
                            if (selectedClientName) {
                                isHighlighted = marker.cliente === selectedClientName;
                            } else if (inactiveClientToShow) {
                                isHighlighted = isMarkerInactive && marker.cliente === inactiveClientToShow.name;
                            } else if (highlightedClientNames) {
                                isHighlighted = highlightedClientNames.includes(marker.cliente);
                            }
                        }

                        const fillOpacity = hasFilter ? (isHighlighted ? 0.95 : 0.12) : 0.95;
                        const strokeOpacity = hasFilter ? (isHighlighted ? 1.0 : 0.15) : 1.0;
                        
                        let fill = 'var(--theme-chart-start, #dc2626)';
                        if (isMarkerInactive) {
                            fill = '#f97316'; // Warning orange for inactive client marker
                        }
                        if (hasFilter && isHighlighted) {
                            fill = isMarkerInactive ? '#ea580c' : 'var(--theme-primary, #b91c1c)';
                        }

                        let toolHtml = '';
                        if (isMarkerInactive) {
                            const daysSince = inactiveClientToShow?.ultimaCompra ? Math.floor(
                                (new Date().getTime() - new Date(inactiveClientToShow.ultimaCompra).getTime()) / (1000 * 60 * 60 * 24)
                            ) : 0;
                            toolHtml = `
                                <div style="font-family:${FONT};min-width:180px;padding:2px 0;">
                                    <div style="font-weight:700;color:var(--text-primary);font-size:14px;margin-bottom:6px;border-bottom:1px solid var(--border-primary);padding-bottom:4px;text-transform:uppercase;">
                                        ${marker.cliente} <span style="color:#f97316;font-size:10px;">(INATIVO)</span>
                                    </div>
                                    <div style="display:flex;justify-content:space-between;font-size:12px;margin:2px 0;color:var(--text-secondary);">
                                        <span>Cidade:</span>
                                        <strong style="color:var(--text-primary);">${marker.cidade} - ${marker.uf}</strong>
                                    </div>
                                    <div style="display:flex;justify-content:space-between;font-size:12px;margin:2px 0;color:var(--text-secondary);">
                                        <span>Última Compra:</span>
                                        <strong style="color:#dc2626;">há ${daysSince} ${daysSince === 1 ? 'dia' : 'dias'}</strong>
                                    </div>
                                    <div style="display:flex;justify-content:space-between;font-size:12px;margin:2px 0;color:var(--text-secondary);">
                                        <span>Histórico Vendas:</span>
                                        <strong style="color:var(--text-primary);">${formatCurrency(marker.totalVenda)}</strong>
                                    </div>
                                </div>
                            `;
                        } else if ((marker as any).isAggregated) {
                            // Tooltip para marcador agregado (visão geral sem filtro)
                            toolHtml = `
                                <div style="font-family:${FONT};min-width:180px;padding:2px 0;">
                                    <div style="font-weight:700;color:var(--text-primary);font-size:14px;margin-bottom:6px;border-bottom:1px solid var(--border-primary);padding-bottom:4px;">
                                        ${marker.cidade} - ${marker.uf}
                                    </div>
                                    <div style="display:flex;justify-content:space-between;font-size:12px;margin:2px 0;color:var(--text-secondary);">
                                        <span>Clientes:</span>
                                        <strong style="color:var(--text-primary);">${(marker as any).totalClientes}</strong>
                                    </div>
                                    <div style="display:flex;justify-content:space-between;font-size:12px;margin:2px 0;color:var(--text-secondary);">
                                        <span>Venda Total:</span>
                                        <strong style="color:var(--text-primary);">${formatCurrency(marker.totalVenda)}</strong>
                                    </div>
                                </div>
                            `;
                        } else {
                            const mcPercent = marker.totalVenda > 0 ? (marker.totalMargem / marker.totalVenda) * 100 : 0;
                            toolHtml = `
                                <div style="font-family:${FONT};min-width:180px;padding:2px 0;">
                                    <div style="font-weight:700;color:var(--text-primary);font-size:14px;margin-bottom:6px;border-bottom:1px solid var(--border-primary);padding-bottom:4px;text-transform:uppercase;">
                                        ${marker.cliente}
                                    </div>
                                    <div style="display:flex;justify-content:space-between;font-size:12px;margin:2px 0;color:var(--text-secondary);">
                                        <span>Cidade:</span>
                                        <strong style="color:var(--text-primary);">${marker.cidade} - ${marker.uf}</strong>
                                    </div>
                                    <div style="display:flex;justify-content:space-between;font-size:12px;margin:2px 0;color:var(--text-secondary);">
                                        <span>Venda Total:</span>
                                        <strong style="color:var(--text-primary);">${formatCurrency(marker.totalVenda)}</strong>
                                    </div>
                                    <div style="display:flex;justify-content:space-between;font-size:12px;margin:2px 0;color:var(--text-secondary);">
                                        <span>M.C. %:</span>
                                        <strong style="color:#16a34a;">${mcPercent.toFixed(1)}%</strong>
                                    </div>
                                    <div style="display:flex;justify-content:space-between;font-size:12px;margin:2px 0;color:var(--text-secondary);">
                                        <span>Participação:</span>
                                        <strong style="color:#2563eb;">${(marker.participacao * 100).toFixed(2)}%</strong>
                                    </div>
                                </div>
                            `;
                        }

                        return (
                            <Marker
                                key={`client-marker-${index}`}
                                coordinates={marker.coordinates}
                                style={{
                                    default: { outline: "none" },
                                    hover: { outline: "none" },
                                    pressed: { outline: "none" }
                                }}
                            >
                                <circle
                                    r={finalRadius}
                                    fill={fill}
                                    fillOpacity={fillOpacity}
                                    stroke="#ffffff"
                                    strokeOpacity={strokeOpacity}
                                    strokeWidth={0.5}
                                    style={{ vectorEffect: 'non-scaling-stroke' }}
                                    className="regions-map-marker"
                                    data-tooltip-id="regions-map-tooltip"
                                    data-tooltip-html={toolHtml}
                                />
                            </Marker>
                        );
                    })}
                </ZoomableGroup>
            </ComposableMap>

            <ReactTooltip
                id="regions-map-tooltip"
                style={{
                    backgroundColor: 'var(--bg-widget)',
                    color: 'var(--text-primary)',
                    borderRadius: 'var(--radius-base, 12px)',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)',
                    border: '1px solid var(--border-primary)',
                    padding: '12px',
                    zIndex: 99999
                }}
            />

            <style>{`
                .regions-map-marker {
                    cursor: pointer;
                    transition: transform 0.15s ease, fill-opacity 0.15s ease;
                    filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.35));
                    outline: none;
                }
                .regions-map-marker:hover {
                    fill-opacity: 1;
                    transform: scale(1.3);
                }
                .regions-map-marker:focus,
                .regions-map-marker:active {
                    outline: none;
                    fill: var(--theme-chart-start, #dc2626);
                }
            `}</style>
        </div>
    );
};

export default RegionsMap;
